import React from "react";
import {
  HiOutlineClipboardList,
  HiOutlineCog,
  HiOutlineCube,
  HiOutlineDocumentText,
  HiOutlineExclamation,
  HiOutlineLightningBolt,
  HiOutlineSearch,
  HiOutlineTrendingUp,
} from "react-icons/hi";

const INPUTS = [
  "Production orders",
  "Demand forecasts",
  "BOM & routing sheets",
  "MES machine schedules",
  "WMS inventory data",
];

const OUTPUTS = [
  "Production schedule",
  "Exception reports",
  "Approval tasks",
  "ERP / MES updates",
  "Planning audit log",
];

const CHECKPOINTS = [
  { label: "Planning Manager Review", startCol: 6 },
  { label: "Schedule Release Approval", startCol: 8 },
];

const STEPS = [
  {
    id: 1,
    title: "Order Capture",
    icon: HiOutlineDocumentText,
    tasks: [
      "Read orders from ERP / MES",
      "Normalize demand signals",
      "Create planning case",
    ],
    agent: "Intake Agent",
    agentRole: "Capture & Extract",
  },
  {
    id: 2,
    title: "BOM Validation",
    icon: HiOutlineClipboardList,
    tasks: [
      "Validate bill of materials",
      "Check revisions & alternates",
      "Flag missing components",
    ],
    agent: "Validation Agent",
    agentRole: "Validate & Check",
  },
  {
    id: 3,
    title: "Capacity Check",
    icon: HiOutlineCog,
    tasks: [
      "Review line & machine load",
      "Check shift calendars",
      "Identify bottlenecks",
    ],
    agent: "Capacity Agent",
    agentRole: "Analyze & Score",
  },
  {
    id: 4,
    title: "Material Availability",
    icon: HiOutlineCube,
    tasks: [
      "Confirm stock on hand",
      "Check open purchase orders",
      "Flag shortages & lead times",
    ],
    agent: "Supply Agent",
    agentRole: "Match & Compare",
  },
  {
    id: 5,
    title: "Plan Generation",
    icon: HiOutlineLightningBolt,
    tasks: [
      "Build feasible schedule",
      "Apply planning rules",
      "Optimize sequence & dates",
    ],
    agent: "Planner Agent",
    agentRole: "Plan & Optimize",
  },
  {
    id: 6,
    title: "Exception Resolution",
    icon: HiOutlineSearch,
    tasks: [
      "Summarize root causes",
      "Route for human review",
      "Track resolution status",
    ],
    agent: "Exception Agent",
    agentRole: "Resolve & Explain",
  },
  {
    id: 7,
    title: "Schedule Release",
    icon: HiOutlineExclamation,
    tasks: [
      "Prepare release proposal",
      "Send approval notifications",
      "Capture sign-off comments",
    ],
    agent: "Action Agent",
    agentRole: "Route & Release",
  },
  {
    id: 8,
    title: "Posting & Audit",
    icon: HiOutlineTrendingUp,
    tasks: [
      "Update ERP / MES / APS",
      "Maintain audit evidence",
      "Generate planning insights",
    ],
    agent: "Audit Agent",
    agentRole: "Post & Report",
  },
];

const SYSTEMS = ["SAP", "Oracle", "Dynamics 365", "MES", "APS", "WMS", "PLM", "Data Lake"];

const styles = `
  .mfg-diag {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #dbe7f7;
    font-family: Roboto, sans-serif;
  }
  .mfg-diag-head {
    background: #1d3f7d;
    padding: 14px 16px 12px;
    text-align: center;
  }
  .mfg-diag-head h4 {
    font-family: Exo, sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 4px;
    line-height: 1.25;
  }
  .mfg-diag-head p {
    margin: 0;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }
  .mfg-diag-hitl {
    background: #f4f7fb;
    padding: 10px 12px 6px;
    border-bottom: 1px solid #e2eaf4;
  }
  .mfg-diag-hitl-label {
    font-family: Exo, sans-serif;
    font-size: 9px;
    font-weight: 700;
    color: #c45d1e;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: center;
    margin: 0 0 8px;
  }
  .mfg-diag-hitl-row {
    display: grid;
    grid-template-columns: 108px repeat(8, minmax(0, 1fr)) 108px;
    gap: 4px;
    min-width: 0;
  }
  .mfg-diag-hitl-pill {
    grid-column: span 2;
    background: linear-gradient(180deg, #f5a84c 0%, #e8913a 100%);
    color: #fff;
    font-family: Exo, sans-serif;
    font-size: 9px;
    font-weight: 600;
    text-align: center;
    padding: 6px 4px;
    border-radius: 6px;
    line-height: 1.25;
    box-shadow: 0 2px 4px rgba(232, 145, 58, 0.3);
  }
  .mfg-diag-body {
    display: grid;
    grid-template-columns: 108px repeat(8, minmax(0, 1fr)) 108px;
    gap: 4px;
    padding: 8px 6px 6px;
    background: #eef3f9;
    overflow-x: auto;
  }
  .mfg-diag-io {
    background: #0e2a5a;
    border-radius: 6px;
    padding: 10px 8px;
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }
  .mfg-diag-io h5 {
    font-family: Exo, sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 10px;
    text-align: center;
    writing-mode: horizontal-tb;
  }
  .mfg-diag-io ul {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .mfg-diag-io li {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-size: 9px;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.92);
  }
  .mfg-diag-io li::before {
    content: "";
    flex: 0 0 10px;
    width: 10px;
    height: 12px;
    margin-top: 1px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 2px;
  }
  .mfg-diag-step {
    background: #fff;
    border-radius: 6px;
    border: 1px solid #d6e2f0;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }
  .mfg-diag-step-head {
    background: #1d3f7d;
    color: #fff;
    padding: 6px 4px;
    text-align: center;
    min-height: 42px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
  }
  .mfg-diag-step-num {
    font-family: Exo, sans-serif;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }
  .mfg-diag-step-title {
    font-family: Exo, sans-serif;
    font-size: 8px;
    font-weight: 600;
    line-height: 1.2;
    padding: 0 2px;
  }
  .mfg-diag-step-icon {
    display: flex;
    justify-content: center;
    padding: 8px 4px 4px;
    color: #1d3f7d;
    font-size: 18px;
  }
  .mfg-diag-step-tasks {
    flex: 1;
    margin: 0;
    padding: 0 6px 8px 14px;
    font-size: 8px;
    line-height: 1.45;
    color: #2a3a4c;
  }
  .mfg-diag-step-tasks li {
    margin-bottom: 3px;
  }
  .mfg-diag-step-tasks li:last-child {
    margin-bottom: 0;
  }
  .mfg-diag-step-agent {
    background: #d4edda;
    border-top: 1px solid #b8dfc4;
    padding: 5px 4px;
    text-align: center;
    margin-top: auto;
  }
  .mfg-diag-step-agent strong {
    display: block;
    font-family: Exo, sans-serif;
    font-size: 8px;
    font-weight: 700;
    color: #1a5c34;
    line-height: 1.2;
  }
  .mfg-diag-step-agent span {
    display: block;
    font-size: 7px;
    color: #2d6b42;
    line-height: 1.2;
    margin-top: 1px;
  }
  .mfg-diag-systems {
    background: #1d3f7d;
    padding: 10px 14px;
    text-align: center;
  }
  .mfg-diag-systems h5 {
    font-family: Exo, sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
  }
  .mfg-diag-systems-tags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-bottom: 6px;
  }
  .mfg-diag-systems-tags span {
    font-size: 9px;
    font-weight: 500;
    color: #1d3f7d;
    background: #fff;
    border-radius: 4px;
    padding: 3px 8px;
  }
  .mfg-diag-systems p {
    margin: 0;
    font-size: 8px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.4;
  }
  .mfg-diag-scroll {
    overflow-x: auto;
    scrollbar-width: thin;
  }
  @media (max-width: 900px) {
    .mfg-diag-hitl-row,
    .mfg-diag-body {
      min-width: 880px;
    }
  }
`;

function CheckpointPill({ label, startCol }) {
  return (
    <div
      className="mfg-diag-hitl-pill"
      style={{ gridColumn: `${startCol} / span 2` }}
    >
      {label}
    </div>
  );
}

export default function ManufacturingProcessFlow({ subIndustry }) {
  return (
    <div
      className="mfg-diag"
      role="img"
      aria-label={`Production order planning automation for ${subIndustry}`}
    >
      <style>{styles}</style>

      <header className="mfg-diag-head">
        <h4>AI-Powered Production Order Planning Automation</h4>
        <p>End-to-end intelligent planning for {subIndustry}</p>
      </header>

      <div className="mfg-diag-scroll">
        <div className="mfg-diag-hitl">
          <p className="mfg-diag-hitl-label">Human-in-the-loop checkpoints</p>
          <div className="mfg-diag-hitl-row">
            {CHECKPOINTS.map((cp) => (
              <CheckpointPill
                key={cp.label}
                label={cp.label}
                startCol={cp.startCol}
              />
            ))}
          </div>
        </div>

        <div className="mfg-diag-body">
          <aside className="mfg-diag-io" aria-label="Inputs">
            <h5>Inputs</h5>
            <ul>
              {INPUTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>

          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id} className="mfg-diag-step">
                <div className="mfg-diag-step-head">
                  <span className="mfg-diag-step-num">{step.id}</span>
                  <span className="mfg-diag-step-title">{step.title}</span>
                </div>
                <div className="mfg-diag-step-icon" aria-hidden="true">
                  <Icon />
                </div>
                <ul className="mfg-diag-step-tasks">
                  {step.tasks.map((task) => (
                    <li key={task}>{task}</li>
                  ))}
                </ul>
                <footer className="mfg-diag-step-agent">
                  <strong>{step.agent}</strong>
                  <span>{step.agentRole}</span>
                </footer>
              </div>
            );
          })}

          <aside className="mfg-diag-io" aria-label="Outputs">
            <h5>Outputs</h5>
            <ul>
              {OUTPUTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </aside>
        </div>
      </div>

      <footer className="mfg-diag-systems">
        <h5>ERP / Manufacturing Systems</h5>
        <div className="mfg-diag-systems-tags">
          {SYSTEMS.map((sys) => (
            <span key={sys}>{sys}</span>
          ))}
        </div>
        <p>
          System of record for orders, BOM, capacity, inventory, schedules,
          documents and audit evidence.
        </p>
      </footer>
    </div>
  );
}

